import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  type MRT_PaginationState,
} from "material-react-table";
import { PieChart } from "@mui/x-charts/PieChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Link,
  Stack,
  Badge,
  Paper,
  Tooltip,
  IconButton,
  Divider,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SuiteCasesBreakdown from "./components/SuiteCasesBreakdown";
import SuiteMetaViewer from "./components/SuiteMetaViewer";
import SuiteReportViewer from "./components/SuiteReportViewer";
import TcStatRing from "./components/TcStatRing";
import SuiteStatusChip from "./components/SuiteStatusChip";
import TableActionButton from "../../components/table/TableActionButton";
import Title from "../../components/Title";
import { pageHelp } from "../../components/help/pageHelp";
import {
  fetchSuites,
  abortSuite,
  rerunSuite,
  cloneSuite,
  exportSuiteConfig,
} from "../../features/slices/Suite";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { GlobalModal } from "../../components/GlobalModal";
import { useNotification } from "../../context/NotificationContext";
import { JsonEditor } from "json-edit-react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import SuiteLog from "./components/SuiteLog";
import ReportDownloadDialog from "./components/Report";
import { useLoader } from "../../context/LoaderContext";
import { fetchAllureGeneralReport } from "../../features/slices/Report";
import { Suite } from "../../interfaces";
import PopoverCustom from "../../components/Popover";
import ExportDialog from "./components/Export";
import FormattedDateCell from "../../components/FormattedDateCell";

const StyledText = styled("text")(({ theme }) => ({
  fill: theme.palette.text.primary,
  textAnchor: "middle",
  dominantBaseline: "central",
  fontSize: 12,
}));
function PieCenterLabel({ children }: { children: React.ReactNode }) {
  const { width, height, left, top } = useDrawingArea();
  return (
    <StyledText x={left + width / 2} y={top + height / 2}>
      {children}
    </StyledText>
  );
}
const isButtonDisabled = (status: string) => {
  const disabledStatuses = [
    "INIT",
    "IN_PROGRESS",
    "ABORTED",
    "ERROR",
    "PREPARE_EXECUTION",
  ];
  return disabledStatuses.includes(status);
};
const canRerun = (status) =>
  ["FAILED", "ABORTED", "SUCCESS", "ERROR"].includes(status);

const canAbort = (status) =>
  ["INIT", "IN_PROGRESS", "PREPARE_EXECUTION"].includes(status);

const canClone = (status) => !["IN_PROGRESS"].includes(status);
const canEdit = (status) => !["IN_PROGRESS", "ABORTED"].includes(status);
// A draft = a suite created in the wizard but never finished (still INIT, no
// cases committed yet). It can be resumed by reopening the wizard for it.
const isDraft = (row) =>
  row?.status === "INIT" && (row?.totalCases ?? 0) === 0;
const isStorageSuite = (suite) => {
  try {
    const cfgSuite = suite?.meta_data?.config?.suite;
    // Marker set by the storage wizard at creation (covers early drafts too).
    if (cfgSuite?.source === "storage") return true;
    const cases = cfgSuite?.cases;
    if (cases?.type === "storage") return true;
    if (cases?.bucket && cases?.path) return true;
    if (cases?.repository || suite?.meta_data?.host) return false;
    return false;
  } catch {
    return false;
  }
};

const Suites = () => {
  const [metaModal, setMetaModal] = useState(false);
  const [logModal, setLogModal] = useState(false);
  const [logId, setLogId] = useState<number>();
  const [browseModal, setBrowseModal] = useState(false);
  const [browseId, setBrowseId] = useState<number>();
  const dispatch = useDispatch<AppDispatch>();
  const [data, setData] = useState<Suite[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [selectedMetadata, setSelectedMetadata] = useState<object>({});
  const [exportModal, setExportModal] = useState(false);
  const [reportModal, setReportModal] = useState(false);
  const [reportId, setReportId] = useState<number>(undefined);
  const [rowCount, setRowCount] = useState(1);
  const [currentRowId, setCurrentRowId] = useState<number>(undefined);
  const navigate = useNavigate();
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: "id",
      desc: true,
    },
  ]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const { notify } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const { user } = useSelector((state: RootState) => state.auth);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleOpen = (event: React.MouseEvent<HTMLElement>, suitId: number) => {
    setCurrentRowId(suitId);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  const handleEditSuite = (id: number) => {
    navigate(`/suites/${id}/edit`);
  };

  const handleResumeSuite = (suite: any) => {
    // Reopen the correct wizard for this draft and continue from the framework
    // step. Storage/artifact drafts must resume into the artifact wizard, not
    // the Git "link" one.
    const id = Number(suite.id);
    if (isStorageSuite(suite)) navigate(`/suites/create/artifact/${id}`);
    else navigate(`/suites/create/link/${id}`);
  };
  const handleViewSuite = (id: number) => {
    navigate(`/suites/${id}/view`);
  };
  const handleClone = (id: number | string) => {
    dispatch(cloneSuite(id))
      .unwrap()
      .then(() => {
        notify("Suite cloned successfully", "success");
        fetchData();
      })
      .catch((error) => {
        console.error("Error:", error);
        notify("Failed to clone suite", "error");
      });
  };
  const handleSuiteExport = (id: number | string) => {
    dispatch(exportSuiteConfig(id))
      .unwrap()
      .then((responseData) => {
        let blobData;
        if (responseData instanceof Blob) {
          blobData = responseData;
        } else if (typeof responseData === "object") {
          const jsonString = JSON.stringify(responseData, null, 4);
          blobData = new Blob([jsonString], { type: "application/json" });
        } else {
          blobData = new Blob([responseData], { type: "application/json" });
        }

        const fileName = `config_suite_${id}.json`;
        const url = window.URL.createObjectURL(blobData);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(url);

        notify("Suite exported successfully", "success");
        fetchData();
      })
      .catch((error) => {
        console.error("Error exporting suite config:", error);
        notify("Failed to export suite", "error");
      });
  };

  const handleCreateOpen = () => navigate("/suites/create", { replace: true });
  const handleMetaOpen = (metadata: object) => {
    setSelectedMetadata(metadata);
    setMetaModal(true);
  };
  const handleReportOpen = (id: number) => {
    setReportModal(true);
    setReportId(id);
  };

  const handleAllure = async (suiteId: number, regenerate = true) => {
    if (!user) {
      notify("User is not authenticated", "error");
      return;
    }
    handleClose();
    notify(
      "Generating the Allure report in the background — it will appear in your Notifications (bell, top-right) with a link when it's ready.",
      "info"
    );
    try {
      await dispatch(
        fetchAllureGeneralReport({
          suiteId: suiteId,
          userId: user?.id,
          regenerate: regenerate,
        })
      ).unwrap();
    } catch (error) {
      console.error("Error:", error);
    }
  };
  const handleAbortSuite = async (id) => {
    showLoader();
    try {
      await dispatch(abortSuite(id)).unwrap();
      notify("Suite aborted successfully", "success");
      fetchData();
    } catch (error) {
      notify("Failed to abort suite", "error");
    } finally {
      hideLoader();
    }
  };

  const handleRerunSuite = async (id) => {
    showLoader();
    try {
      await dispatch(rerunSuite(id)).unwrap();
      notify("Suite prepared for re-run", "success");
      fetchData();
    } catch (error) {
      notify("Failed to re-run suite", "error");
    } finally {
      hideLoader();
    }
  };
  const fetchData = useCallback(
    debounce(async () => {
      setIsLoading(true);
      setIsRefetching(true);

      const params = {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
        sort_by: sorting[0]?.id || "id",
        order: sorting[0]?.desc ? "desc" : "asc",
        filter_by: JSON.stringify(columnFilters) || "",
        globalFilter: globalFilter || "",
      };

      const query = new URLSearchParams(params).toString();

      try {
        const suitesResponse = await dispatch(fetchSuites(query)).unwrap();

        const enrichedSuites = suitesResponse.suites.map((suite) => {
          const cases = suite.cases || [];
          const statusCounts = cases.reduce((acc, caseItem) => {
            acc[caseItem.tc_result] = (acc[caseItem.tc_result] || 0) + 1;
            return acc;
          }, {});
          const sysCounts = cases.reduce((acc, caseItem) => {
            acc[caseItem.sys_result] = (acc[caseItem.sys_result] || 0) + 1;
            return acc;
          }, {});
          const totalCases = cases.length;
          const passedCases = statusCounts["PASSED"] || 0;
          const sysSuccess = sysCounts["SUCCESS"] || 0;
          const progress =
            totalCases > 0 ? (passedCases / totalCases) * 100 : 0;
          const sys_progress =
            totalCases > 0 ? (sysSuccess / totalCases) * 100 : 0;

          return {
            ...suite,
            cases,
            statusCounts,
            progress,
            totalCases,
            sys_progress,
            step: suite.meta_data.step || "N/A",
            report_exists: suite.report_exists,
            passed: statusCounts["PASSED"] || 0,
            failed: statusCounts["FAILED"] || 0,
            init: statusCounts["INIT"] || 0,
            interrupted: statusCounts["INTERRUPTED"] || 0,
            internal_error: statusCounts["INTERNAL ERROR"] || 0,
            cmd_error: statusCounts["CMD_ERROR"] || 0,
            no_tests: statusCounts["NO TESTS"] || 0,
            reserved: statusCounts["RESERVED"] || 0,
            success: sysCounts["SUCCESS"] || 0,
            in_queue: sysCounts["IN_QUEUE"] || 0,
            sys_failed: sysCounts["FAILED"] || 0,
          };
        });

        setData(enrichedSuites);
        setRowCount(suitesResponse.pages);
      } catch (error) {
        console.error("Error fetching suites:", error);
        setIsError(true);
      } finally {
        setIsRefetching(false);
        setIsLoading(false);
      }
    }, 300),
    [
      pagination.pageIndex,
      pagination.pageSize,
      sorting,
      globalFilter,
      columnFilters,
      dispatch,
    ]
  );

  useEffect(() => {
    fetchData();
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    globalFilter,
    columnFilters,
    dispatch,
  ]);
  const columns = useMemo<MRT_ColumnDef<Suite>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "allure",
        header: "Allure",
        enableColumnFilter: false,
        enableSorting: false,
        width: 50,
        Cell: ({ row }) => (
          <TableActionButton
            label="General"
            icon={<AssessmentOutlinedIcon />}
            onClick={(event) => handleOpen(event, Number(row.id))}
            disabled={isButtonDisabled(row.original.status)}
            tooltip="Generate / open the Allure report"
          />
        ),
      },
      {
        accessorKey: "title",
        header: "Title",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <SuiteStatusChip
            status={row.original.status}
            draft={isDraft(row.original)}
            passed={Number(row.original.passed) || 0}
            total={Number(row.original.totalCases) || 0}
          />
        ),
      },
      {
        enableColumnFilter: false,
        accessorKey: "created_at",
        header: "Created At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        id: "tc_result",
        header: "Cases",
        enableColumnFilter: false,
        enableSorting: false,

        accessorFn(originalRow: any) {
          return originalRow.totalCases;
        },

        Cell: ({ row }) => (
          <Badge
            color="primary"
            badgeContent={row.original.totalCases}
            showZero
          >
            <Button onClick={() => row.toggleExpanded()}>
              {row.getIsExpanded() ? "Collapse" : "Expand"}
            </Button>
          </Badge>
        ),
        sortFn: (rowA: any, rowB: any) =>
          rowA.values.tc_result > rowB.values.tc_result ? 1 : -1,
      },
      {
        accessorKey: "progress",
        header: "TC Stat",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <TcStatRing
            passed={Number(row.original?.passed) || 0}
            failed={Number(row.original?.failed) || 0}
            total={Number(row.original?.totalCases) || 0}
          />
        ),
      },
      {
        accessorKey: "step",
        header: "Step",
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const statusColorMap: Record<string, string> = {
            INITIALIZE: "default",
            FRAMEWORK_REGISTRY: "primary",
            FRAMEWORK_FETCH: "info",
            FRAMEWORK_PROCESS: "warning",
            CASES_REGISTRY: "primary",
            CASES_FETCH: "info",
            CASES_PROCESS: "warning",
            SOURCES_FETCH: "primary",
            SOURCES_PROCESS: "info",
            SUCCESS: "success",
          };

          const status: keyof typeof statusColorMap = row.original.step;
          const color = statusColorMap[status] || "default";

          return (
            <Chip
              color={
                color as
                  | "default"
                  | "primary"
                  | "secondary"
                  | "error"
                  | "info"
                  | "success"
                  | "warning"
              }
              label={status}
              sx={{ minWidth: 110, height: 26, fontSize: "0.72rem", fontWeight: 700 }}
            />
          );
        },
      },
      {
        accessorKey: "planed_start",
        header: "Planed Start",
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "report",
        header: "Report",
        enableColumnFilter: false,
        enableSorting: false,
        width: 50,
        Cell: ({ row }) => {
          const available =
            (row.original.totalCases ?? 0) > 0 && !isDraft(row.original);
          return (
            <TableActionButton
              label="Report"
              icon={<FileDownloadOutlinedIcon />}
              onClick={() => handleReportOpen(Number(row.id))}
              disabled={!available}
              tooltip={
                available ? "Download suite report" : "No cases to report yet"
              }
            />
          );
        },
      },
      {
        accessorKey: "stats",
        header: "Statistic",
        enableColumnFilter: false,
        enableSorting: false,
        width: 50,
        Cell: ({ row }) => (
          <Button onClick={() => handleViewSuite(Number(row.id))}>View</Button>
        ),
      },
      {
        accessorKey: "meta_data",
        header: "Meta",
        enableColumnFilter: false,
        enableSorting: false,
        width: 50,
        Cell: ({ cell }) => (
          <TableActionButton
            label="View"
            variant="text"
            icon={<VisibilityOutlinedIcon />}
            onClick={() => handleMetaOpen(cell.getValue<any>())}
          />
        ),
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    mrtTheme: (theme) => ({
      baseBackgroundColor: theme.palette.background.default,
    }),
    muiTableContainerProps: {
      sx: { maxHeight: "calc(78vh - 112px)" },
    },
    columnFilterDisplayMode: "popover",
    muiPaginationProps: {
      color: "secondary",
      rowsPerPageOptions: [10, 25, 50, 100],
      shape: "rounded",
      variant: "outlined",
    },
    paginationDisplayMode: "pages",
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        right: ["mrt-row-actions"],
      },
      columnVisibility: { step: false, stats: false },
    },
    enableColumnResizing: true,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    enableGrouping: false,
    enableRowSelection: false,
    enableRowActions: true,
    enableColumnOrdering: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnFilterModes: true,
    enableColumnFilters: true,
    positionActionsColumn: "last",
    enableFacetedValues: true,
    getRowId: (row) => String(row.id),
    layoutMode: "grid-no-grow",
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    rowCount,
    pageCount: rowCount,
    state: {
      pagination,
      columnFilters,
      globalFilter,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      sorting,
    },

    renderDetailPanel: ({ row }: { row: any }) => (
      <SuiteCasesBreakdown row={row.original} />
    ),
      renderTopToolbarCustomActions: ({ table }) => (
      <Box>
        <Button variant="contained" onClick={() => handleCreateOpen()}>
          Create New Suite
        </Button>
        <Button
          variant="outlined"
          sx={{ ml: 2 }}
          onClick={() => setExportModal(true)}
        >
          Export
        </Button>
        <Tooltip arrow title="Refresh Data">
          <IconButton onClick={() => fetchData()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    renderRowActionMenuItems: ({ row }) => [
      ...(isDraft(row.original)
        ? [
            <MenuItem
              key="resume"
              onClick={() => handleResumeSuite(row.original)}
              sx={{ m: 0, fontWeight: 700, color: "primary.main" }}
            >
              Resume draft
            </MenuItem>,
          ]
        : []),
      <MenuItem
        key="edit"
        onClick={() => handleEditSuite(Number(row.id))}
        sx={{ m: 0 }}
        disabled={!canEdit(row.original.status)}
      >
        Edit
      </MenuItem>,

      <Tooltip
        key="log"
        title={
          isStorageSuite(row.original)
            ? "Log not available for storage suite"
            : ""
        }
        placement="left"
      >
        <span>
          <MenuItem
            onClick={() => {
              setLogId(Number(row.id));
              setLogModal(true);
            }}
            sx={{ m: 0 }}
            disabled={isStorageSuite(row.original)}
          >
            Log
          </MenuItem>
        </span>
      </Tooltip>,

      <MenuItem
        key="browse-report"
        onClick={() => {
          setBrowseId(Number(row.id));
          setBrowseModal(true);
        }}
        sx={{ m: 0 }}
        disabled={(row.original.totalCases ?? 0) === 0 || isDraft(row.original)}
      >
        Browse report
      </MenuItem>,

      <Tooltip
        key="clone"
        title={!canClone(row.original.status) ? "Can't clone while the suite is running" : ""}
        placement="left"
      >
        <span>
          <MenuItem
            onClick={() => handleClone(Number(row.id))}
            sx={{ m: 0 }}
            disabled={!canClone(row.original.status)}
          >
            Clone
          </MenuItem>
        </span>
      </Tooltip>,

      <MenuItem
        key="abort"
        onClick={() => handleAbortSuite(Number(row.id))}
        disabled={!canAbort(row.original.status)}
      >
        Abort
      </MenuItem>,

      <MenuItem
        key="rerun"
        onClick={() => handleRerunSuite(Number(row.id))}
        disabled={!canRerun(row.original.status)}
      >
        Re-Run
      </MenuItem>,

      <MenuItem
        key="config"
        onClick={() => handleSuiteExport(Number(row.id))}
        sx={{ m: 0 }}
        // Storage suites carry their config in meta_data.config too, and the
        // /suite/<id>/export/config endpoint is type-agnostic, so allow the
        // download for them as well (only gate on transient statuses).
        disabled={isButtonDisabled(row.original.status)}
      >
        Config
      </MenuItem>,
    ],
  });

  return (
    <>
      <Title help={pageHelp.suites.help} helpItems={pageHelp.suites.items}>
        Suites Overview
      </Title>
      <MaterialReactTable table={table} />
      <GlobalModal
        open={metaModal}
        onClose={() => setMetaModal(false)}
        title="Suite details"
        height="auto"
      >
        <Typography
          color="text.secondary"
          sx={{ fontSize: "0.85rem", mb: 2.5, mt: -1 }}
        >
          Repositories, branches and build info captured when this suite was
          created. Secrets are masked.
        </Typography>
        <SuiteMetaViewer data={selectedMetadata} />
      </GlobalModal>
      <ExportDialog open={exportModal} onClose={() => setExportModal(false)} />
      <GlobalModal
        open={logModal}
        onClose={() => setLogModal(false)}
        title="Suite Log"
        height="auto"
      >
        <SuiteLog suiteId={logId} />
      </GlobalModal>
      <GlobalModal
        open={browseModal}
        onClose={() => setBrowseModal(false)}
        title="Browse suite report"
      >
        <SuiteReportViewer suiteId={browseId} />
      </GlobalModal>

      <ReportDownloadDialog
        suiteId={reportId}
        open={reportModal}
        onClose={() => setReportModal(false)}
      />
      <PopoverCustom
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2, width: 300 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", mb: 0.5 }}>
            Allure report
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1.5, lineHeight: 1.4 }}
          >
            The report is built in the background. When it's ready, a
            notification with a link appears in the&nbsp;
            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
              bell menu (top-right)
            </Box>
            .
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<AutorenewIcon />}
              onClick={() => handleAllure(currentRowId, true)}
            >
              Regenerate
            </Button>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={() => handleAllure(currentRowId, false)}
            >
              Use existing
            </Button>
          </Stack>
        </Box>
      </PopoverCustom>
    </>
  );
};

export default Suites;
